import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { toPng } from 'html-to-image';
import { lazy, type MouseEvent, Suspense, useCallback } from 'react';
import {
  Background,
  Controls,
  addEdge,
  getRectOfNodes,
  getTransformForBounds,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';
import { useTranslation } from 'react-i18next';

import { GenericModalV2 } from 'components/shared';

import useGetGanttDiagram from './hooks/useGetGanttDiagram';
import { CustomNode, updateEdgeStyle, updateNodeStyle } from '../../utils';

const ReactFlow = lazy(() => import(/* webpackPrefetch: true */ 'reactflow'));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  id: number;
  name: string;
}

// Constantes para dimensiones PDF (A4 landscape)
const PDF_WIDTH_A4_LANDSCAPE = 842;
const PDF_HEIGHT_A4_LANDSCAPE = 595;
const NODES_PER_PAGE = 8;
const CANVAS_WIDTH_PER_PAGE = 1684;
const CANVAS_HEIGHT_PER_PAGE = 1190;
const MIN_SCALE = 0.5;
const MAX_SCALE = 2;
const GRAPH_HEIGHT = 700;

export const styles = {
  modal: {
    maxWidth: 'none',
    width: '80%',
    maxHeight: 1000,
    height: '85%'
  }
};

export default function StateFlowGraph({ isOpen, onClose, id, name }: Props) {
  const { getNodes } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  useGetGanttDiagram({ id, setNodes, setEdges });

  const { t } = useTranslation('global');

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges]
  );

  /**
   * Descarga el PDF generado
   */
  const downloadPDF = useCallback(
    (pdfDocGenerator: ReturnType<typeof window.pdfMake.createPdf>) => {
      pdfDocGenerator.getBlob((blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      });
    },
    [name]
  );

  /**
   * Genera y descarga el gráfico como PDF
   */
  const handleExportToPDF = useCallback(async () => {
    const viewportElement = document.querySelector('.react-flow__viewport');
    if (!viewportElement) return;

    const pagesNeeded = Math.max(1, Math.floor(nodes.length / NODES_PER_PAGE));
    const canvasWidth = pagesNeeded * CANVAS_WIDTH_PER_PAGE;
    const canvasHeight = pagesNeeded * CANVAS_HEIGHT_PER_PAGE;

    const nodesBounds = getRectOfNodes(getNodes());
    const transform = getTransformForBounds(
      nodesBounds,
      canvasWidth,
      canvasHeight,
      MIN_SCALE,
      MAX_SCALE
    );

    try {
      const dataUrl = await toPng(viewportElement, {
        backgroundColor: 'white',
        width: canvasWidth,
        height: canvasHeight,
        style: {
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          transform: `translate(${transform[0]}px, ${transform[1]}px) scale(${transform[2]})`
        }
      });

      const { pdfMake } = window;
      const pdfDocGenerator = pdfMake.createPdf({
        pageOrientation: 'landscape',
        content: [
          {
            image: dataUrl,
            width: PDF_WIDTH_A4_LANDSCAPE,
            height: PDF_HEIGHT_A4_LANDSCAPE
          }
        ],
        pageMargins: [0, 0, 0, 0]
      });

      downloadPDF(pdfDocGenerator);
    } catch (error) {
      console.error('Error al generar PDF:', error);
    }
  }, [nodes.length, getNodes, downloadPDF]);

  const onNodeClick = useCallback(
    (event: MouseEvent, node: CustomNode) => {
      const selectedColor = node?.data?.color;

      setNodes((nds) => updateNodeStyle(nds, node, selectedColor));
      setEdges((eds) => updateEdgeStyle(eds, node, selectedColor));
    },
    [setNodes, setEdges]
  );

  // Constantes para límites de arrastre de nodos
  const NODE_DRAG_MIN_X = 0;
  const NODE_DRAG_MIN_Y = 0;
  const NODE_DRAG_MAX_X = 1500;
  const NODE_DRAG_MAX_Y = 1500;

  /**
   * Maneja el arrastre de nodos limitando su posición dentro de los límites definidos
   */
  const handleNodeDrag = useCallback(
    (event: MouseEvent, node: CustomNode) => {
      const newPosition = {
        x: Math.max(
          NODE_DRAG_MIN_X,
          Math.min(node.position.x, NODE_DRAG_MAX_X)
        ),
        y: Math.max(
          NODE_DRAG_MIN_Y,
          Math.min(node.position.y, NODE_DRAG_MAX_Y)
        )
      };

      setNodes((nds) =>
        nds.map((n) => (n.id === node.id ? { ...n, position: newPosition } : n))
      );
    },
    [setNodes]
  );

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      style={styles.modal}
      title={`
      ${t('clients.graphFromFlow')}: ${name}`}
    >
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </div>
        }
      >
        <div
          style={{
            height: `${GRAPH_HEIGHT}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          <Button
            color="error"
            onClick={handleExportToPDF}
            sx={{
              position: 'absolute',
              right: 60,
              top: 10,
              zIndex: 1000
            }}
            variant="contained"
          >
            {t('ticket.messages.saveAsPDF')}
          </Button>
          <ReactFlow
            className="intersection-flow"
            edges={edges}
            nodes={nodes}
            nodesConnectable={false}
            onConnect={onConnect}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onNodesChange={onNodesChange}
            onNodeDrag={handleNodeDrag}
            fitView
          >
            <Background color="#aaa" gap={16} size={1} />
            <Controls position="top-right" showInteractive={false} />
          </ReactFlow>
        </div>
      </Suspense>
    </GenericModalV2>
  );
}
